import React, { useMemo, useState } from 'react';
import type {
  GpCategory,
  GpCategoryId,
  GpGap,
  GpKpi,
  GpPhotoAgeLevel,
  GpPrompt,
  GpTone,
} from '../../growth-gbp-photos/types';
import { GP_CATEGORIES, GP_KPIS, GP_PROMPTS } from '../../growth-gbp-photos/mockData';
import { GP_PRIORITY_LABEL, GROWTH_GBP_PHOTOS_META } from '../../growth-gbp-photos/config';
import { Icon } from '../Icon/Icon';
import './growth-gbp-photos.css';

const TONE: Record<GpTone, { accent: string; tint: string }> = {
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: {
    accent: 'var(--terracotta)',
    tint: 'color-mix(in srgb, var(--terracotta) 12%, transparent)',
  },
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  neutral: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

const eyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

const GAP_TONE: Record<GpGap, GpTone> = {
  critical: 'terra',
  high: 'terra',
  medium: 'gold',
  low: 'success',
};

const PRIORITY_TONE: Record<GpPrompt['priority'], GpTone> = {
  essential: 'terra',
  recommended: 'gold',
  nice: 'neutral',
};

function ageLevel(days: number | null): GpPhotoAgeLevel | null {
  if (days === null) return null;
  if (days < 30) return 'fresh';
  if (days < 90) return 'aging';
  return 'stale';
}

const AGE_TONE: Record<GpPhotoAgeLevel, GpTone> = {
  fresh: 'success',
  aging: 'gold',
  stale: 'terra',
};

function GpCard({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

function GpKpiTile({ kpi }: { kpi: GpKpi }) {
  const accent = kpi.tone === 'neutral' ? 'var(--ink-0)' : TONE[kpi.tone].accent;
  return (
    <GpCard className="px-[18px] py-[15px]">
      <div className={eyebrowClass}>{kpi.label}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <span
          className="font-[var(--font-display)] text-[28px] font-extrabold leading-none tracking-[-0.04em]"
          style={{ color: accent }}
        >
          {kpi.value}
        </span>
        {kpi.unit ? <span className="text-[13px] text-[var(--ink-4)]">{kpi.unit}</span> : null}
      </div>
      <div className="mt-2 text-[12px] text-[var(--ink-3)]">{kpi.sub}</div>
    </GpCard>
  );
}

function GpCategoryCard({
  category,
  active,
  onSelect,
}: {
  category: GpCategory;
  active: boolean;
  onSelect: () => void;
}) {
  const total = category.uploaded + category.additional;
  const pct = Math.round((category.uploaded / category.count) * 100);
  const barAccent = TONE[GAP_TONE[category.gap]].accent;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        'cursor-pointer rounded-[var(--r-md)] border bg-[var(--cream-1)] px-4 py-3 text-left transition',
        active
          ? '!border-[color-mix(in_srgb,var(--success)_45%,transparent)] bg-[var(--success-tint)]'
          : 'border-[var(--cream-2)]',
      ].join(' ')}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="font-[var(--font-display)] text-[14px] font-semibold text-[var(--ink-0)]">
          {category.label}
        </span>
        <span className="font-[var(--font-mono)] text-[11px] font-medium text-[var(--ink-3)]">
          {category.uploaded}/{category.count}
          {category.additional > 0 ? (
            <span className="ml-1 text-[var(--success)]">+{category.additional}</span>
          ) : null}
        </span>
      </div>
      <div className="mb-1 h-1 overflow-hidden rounded-full bg-[var(--surface-mute)]">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: barAccent }} />
      </div>
      <div className="text-right text-[10.5px] text-[var(--ink-4)]">
        {total} total photo{total !== 1 ? 's' : ''}
      </div>
    </button>
  );
}

function GpImageTile({
  example,
  label,
  labelTone,
}: {
  example: string;
  label: string;
  labelTone: 'ink' | 'ink-2';
}) {
  return (
    <div className="relative flex aspect-[4/3] items-center justify-center bg-[var(--surface-mute)]">
      <Icon kind="image" size={26} stroke={1.6} className="text-[var(--ink-4)]" />
      <span className="sr-only">{example}</span>
      <span
        className="absolute bottom-2 left-2 rounded-[var(--r-sm)] px-2 py-0.5 text-[10px] font-semibold text-[var(--cream-1)]"
        style={{ background: labelTone === 'ink' ? 'var(--ink-0)' : 'var(--ink-2)' }}
      >
        {label}
      </span>
    </div>
  );
}

function GpAgeBadge({ days }: { days: number }) {
  const level = ageLevel(days);
  if (!level) return null;
  const tone = TONE[AGE_TONE[level]];
  return (
    <span
      className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-[var(--r-sm)] px-2 py-0.5 text-[10px] font-semibold"
      style={{ background: tone.tint, color: tone.accent }}
    >
      <Icon kind={level === 'stale' ? 'alert' : 'clock'} size={10} stroke={2.2} />
      {days}d{level === 'stale' ? ' · stale' : ''}
    </span>
  );
}

function GpPromptCard({
  prompt,
  onUpload,
  onRemove,
}: {
  prompt: GpPrompt;
  onUpload?: () => void;
  onRemove?: () => void;
}) {
  const level = ageLevel(prompt.uploadedDays);
  const stale = level === 'stale';
  const pTone = TONE[PRIORITY_TONE[prompt.priority]];

  return (
    <GpCard
      className={[
        'overflow-hidden !p-0',
        stale ? '!border-[color-mix(in_srgb,var(--terracotta)_40%,transparent)]' : '',
      ].join(' ')}
    >
      <div className="grid grid-cols-2 border-b border-[var(--cream-2)]">
        <div className="relative border-r border-[var(--cream-2)]">
          {prompt.uploaded ? (
            <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden bg-[var(--surface-mute)]">
              {prompt.imageUrl ? (
                <img
                  src={prompt.imageUrl}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <Icon kind="image" size={26} stroke={1.6} className="text-[var(--ink-4)]" />
              )}
              {prompt.uploadedDays !== null ? <GpAgeBadge days={prompt.uploadedDays} /> : null}
              <span
                className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-[var(--r-sm)] px-2 py-0.5 text-[10px] font-semibold"
                style={{ background: 'var(--success-tint)', color: 'var(--success)' }}
              >
                <Icon kind="check" size={10} stroke={2.6} />
                Uploaded
              </span>
              <span className="absolute bottom-2 left-2 rounded-[var(--r-sm)] bg-[var(--ink-0)] px-2 py-0.5 text-[10px] font-semibold text-[var(--cream-1)]">
                Your photo
              </span>
            </div>
          ) : (
            <div className="relative flex aspect-[4/3] flex-col items-center justify-center bg-[var(--surface-mute)] text-[var(--ink-4)]">
              <span className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-[var(--r-md)] border-2 border-dashed border-[var(--cream-2)] bg-[var(--cream-1)]">
                <Icon kind="image" size={20} stroke={1.8} />
              </span>
              <span className="text-[12px] font-medium text-[var(--ink-3)]">No photo yet</span>
              <span className="absolute bottom-2 left-2 rounded-[var(--r-sm)] bg-[var(--ink-0)] px-2 py-0.5 text-[10px] font-semibold text-[var(--cream-1)]">
                Your photo
              </span>
            </div>
          )}
        </div>
        <GpImageTile example={prompt.example} label="Example" labelTone="ink-2" />
      </div>

      {stale ? (
        <div
          className="flex items-center gap-2 border-b px-5 py-2 text-[12px]"
          style={{
            background: TONE.terra.tint,
            borderColor: 'color-mix(in srgb, var(--terracotta) 22%, transparent)',
            color: 'var(--terracotta)',
          }}
        >
          <Icon kind="alert" size={13} stroke={2.2} />
          <span>
            <strong className="font-semibold">Refresh recommended</strong> — photo is{' '}
            {prompt.uploadedDays} days old. Google rewards fresh activity.
          </span>
        </div>
      ) : null}

      <div className="px-5 py-4">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span
            className="inline-flex items-center rounded-[var(--r-pill)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.05em]"
            style={{ background: pTone.tint, color: pTone.accent }}
          >
            {GP_PRIORITY_LABEL[prompt.priority]}
          </span>
          {prompt.requiresConsent ? (
            <span
              className="inline-flex items-center gap-1 rounded-[var(--r-pill)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.05em]"
              style={{ background: TONE.gold.tint, color: 'var(--gold)' }}
            >
              <Icon kind="alert" size={10} stroke={2.2} />
              Patient consent required
            </span>
          ) : null}
        </div>

        <h3 className="font-[var(--font-display)] text-[16px] font-semibold tracking-[-0.01em] text-[var(--ink-0)]">
          {prompt.title}
        </h3>
        <p className="mt-1.5 text-[13px] leading-[1.55] text-[var(--ink-3)]">{prompt.description}</p>

        <div className="mt-3 rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-3.5 py-3">
          <div className={eyebrowClass}>Shooting tips</div>
          <ul className="mt-2 space-y-1">
            {prompt.tips.map((tip) => (
              <li key={tip} className="flex gap-1.5 text-[12px] leading-[1.55] text-[var(--ink-2)]">
                <span className="font-bold text-[var(--success)]">·</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-3 font-[var(--font-mono)] text-[11px] text-[var(--ink-4)]">
          {prompt.dimensions}
        </div>

        <div className="mt-3 flex gap-2">
          {prompt.uploaded ? (
            <>
              <button
                type="button"
                onClick={onUpload}
                className="inline-flex flex-1 cursor-pointer items-center justify-center rounded-[var(--r-sm)] border px-3.5 py-2 text-[13px] font-semibold"
                style={
                  stale
                    ? {
                        borderColor: 'color-mix(in srgb, var(--terracotta) 35%, transparent)',
                        background: TONE.terra.tint,
                        color: 'var(--terracotta)',
                      }
                    : {
                        borderColor: 'var(--cream-2)',
                        background: 'var(--cream-1)',
                        color: 'var(--ink-2)',
                      }
                }
              >
                {stale ? 'Refresh Photo' : 'Replace'}
              </button>
              <button
                type="button"
                onClick={onRemove}
                className="inline-flex cursor-pointer items-center justify-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-2 text-[13px] font-medium text-[var(--ink-2)]"
              >
                Remove
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onUpload}
              className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-[var(--r-sm)] border-0 bg-[var(--success)] px-3.5 py-2.5 text-[13px] font-semibold text-[var(--cream-1)]"
            >
              <Icon kind="upload" size={14} stroke={2} />
              Upload Photo
            </button>
          )}
        </div>
      </div>
    </GpCard>
  );
}

export interface GbpPhotosViewProps {
  categories?: typeof GP_CATEGORIES;
  prompts?: typeof GP_PROMPTS;
  kpis?: typeof GP_KPIS;
  loading?: boolean;
  onBulkUpload?: () => void;
  onViewOnGoogle?: () => void;
  onUploadPhoto?: (promptId: string) => void;
  onRemovePhoto?: (promptId: string) => void;
}

export function GbpPhotosView({
  categories: categoriesProp,
  prompts: promptsProp,
  kpis: kpisProp,
  loading = false,
  onBulkUpload,
  onViewOnGoogle,
  onUploadPhoto,
  onRemovePhoto,
}: GbpPhotosViewProps = {}) {
  const meta = GROWTH_GBP_PHOTOS_META;
  const categories = categoriesProp ?? GP_CATEGORIES;
  const sourcePrompts = promptsProp ?? GP_PROMPTS;
  const kpis = kpisProp ?? GP_KPIS;
  const [activeCategory, setActiveCategory] = useState<GpCategoryId>('essential');

  const prompts = useMemo(
    () => sourcePrompts.filter((prompt) => prompt.category === activeCategory),
    [activeCategory, sourcePrompts],
  );
  const uploadedInCategory = prompts.filter((prompt) => prompt.uploaded).length;

  return (
    <div className="growth-gbp-photos-root relative mx-auto w-full max-w-[1040px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[22px]">
        <div className="mb-2 flex items-center gap-2">
          <Icon kind="image" size={13} stroke={2} className="text-[var(--ai-3)]" />
          <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
            {meta.eyebrow}
          </span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
              {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
            </h1>
            <p className="mt-2.5 max-w-[640px] text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
              {meta.description}
              {loading ? ' · Updating…' : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onBulkUpload}
              className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-2 text-[13px] font-medium text-[var(--ink-2)]"
            >
              <Icon kind="upload" size={15} stroke={1.9} />
              Bulk Upload
            </button>
            <button
              type="button"
              onClick={onViewOnGoogle}
              className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-2 text-[13px] font-medium text-[var(--ink-2)]"
            >
              <Icon kind="external" size={15} stroke={1.9} />
              View on Google
            </button>
          </div>
        </div>
      </div>

      <div
        className="gbp-photos-kpi-grid mb-6 grid gap-3"
        style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}
      >
        {kpis.map((kpi) => (
          <GpKpiTile key={kpi.key} kpi={kpi} />
        ))}
      </div>

      <div
        className="mb-7 flex items-start gap-3.5 rounded-[var(--r-xl)] border px-5 py-4"
        style={{
          borderColor: 'color-mix(in srgb, var(--success) 26%, transparent)',
          background: 'var(--success-tint)',
        }}
      >
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--r-md)] bg-[var(--cream-1)] text-[var(--success)]">
          <Icon kind="info" size={17} stroke={1.9} />
        </span>
        <p className="text-[13px] leading-[1.6] text-[var(--ink-2)]">
          <strong className="font-semibold text-[var(--success)]">Why this matters:</strong> Google
          weighs recent photo activity heavily for Map Pack rankings. Profiles with fresh uploads
          (within 90 days) see a <strong className="font-semibold">27% average boost</strong> in
          local search visibility. ELVA recommends refreshing photos every 60-90 days to maintain
          momentum.
        </p>
      </div>

      <div className="mb-5">
        <div className="mb-3.5">
          <h2 className="m-0 font-[var(--font-display)] text-xl font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
            Photo Categories
          </h2>
          <p className="mt-1 text-[13px] text-[var(--ink-3)]">
            Click a category to see specific recommendations. Numbers show prompts completed +
            additional photos.
          </p>
        </div>
        <div
          className="gbp-photos-cat-grid grid gap-2.5"
          style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' }}
        >
          {categories.map((category) => (
            <GpCategoryCard
              key={category.id}
              category={category}
              active={category.id === activeCategory}
              onSelect={() => {
                setActiveCategory(category.id);
              }}
            />
          ))}
        </div>
      </div>

      {activeCategory === 'beforeafter' ? (
        <div
          className="mb-6 flex items-start gap-3.5 rounded-[var(--r-xl)] border px-5 py-4"
          style={{
            borderColor: 'color-mix(in srgb, var(--gold) 26%, transparent)',
            background: 'var(--gold-tint)',
          }}
        >
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--r-md)] bg-[var(--cream-1)] text-[var(--gold)]">
            <Icon kind="alert" size={17} stroke={1.9} />
          </span>
          <div className="text-[13px] leading-[1.6] text-[var(--ink-2)]">
            <div className="mb-1 font-semibold text-[var(--gold)]">
              Patient consent is required for all before/after photos
            </div>
            <div>
              Before uploading any before/after image, you must have signed written consent from the
              patient authorizing the use of their photos in marketing. Never upload images that
              could identify a patient without explicit permission. Photos should focus only on the
              mouth/teeth — avoid shots showing the patient&apos;s face or identifying features.
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="font-medium text-[var(--gold)]">Need a consent template?</span>
              <span className="inline-flex cursor-pointer items-center gap-1 font-medium text-[var(--success)]">
                Download ELVA&apos;s HIPAA-compliant consent form
                <Icon kind="arrow-r" size={13} stroke={2} />
              </span>
            </div>
          </div>
        </div>
      ) : null}

      <div className="mb-3.5 flex items-end justify-between gap-3">
        <div>
          <h2 className="m-0 font-[var(--font-display)] text-[17px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
            Recommended Photos
          </h2>
          <p className="mt-1 text-[12.5px] text-[var(--ink-3)]">
            ELVA-curated prompts that drive Map Pack ranking in your category
          </p>
        </div>
        <span className="shrink-0 text-[12px] text-[var(--ink-4)]">
          {uploadedInCategory} of {prompts.length} uploaded
        </span>
      </div>

      {prompts.length > 0 ? (
        <div
          className="gbp-photos-prompt-grid grid gap-4"
          style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}
        >
          {prompts.map((prompt) => (
            <GpPromptCard
              key={prompt.id}
              prompt={prompt}
              {...(onUploadPhoto ? { onUpload: () => onUploadPhoto(prompt.id) } : {})}
              {...(onRemovePhoto ? { onRemove: () => onRemovePhoto(prompt.id) } : {})}
            />
          ))}
        </div>
      ) : (
        <GpCard className="flex flex-col items-center px-5 py-12 text-center">
          <Icon kind="image" size={34} stroke={1.5} className="text-[var(--ink-4)]" />
          <div className="mt-3 font-[var(--font-display)] text-[16px] font-semibold text-[var(--ink-1)]">
            No photos in this category yet
          </div>
          <div className="mt-1 text-[13px] text-[var(--ink-3)]">
            Use bulk upload below to add photos.
          </div>
        </GpCard>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-[var(--r-xl)] border-2 border-dashed border-[var(--cream-2)] bg-[var(--surface-mute)] px-6 py-6">
        <div className="flex items-start gap-3.5">
          <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-[var(--r-md)] bg-[var(--success-tint)] text-[var(--success)]">
            <Icon kind="upload" size={22} stroke={1.8} />
          </span>
          <div>
            <div className="font-[var(--font-display)] text-[16px] font-semibold text-[var(--ink-0)]">
              Have a batch of photos ready?
            </div>
            <p className="mt-1 max-w-[560px] text-[13px] leading-[1.55] text-[var(--ink-3)]">
              Upload any number of photos. ELVA&apos;s AI will auto-categorize each one and try to
              match it to our prompts. Unmatched photos are saved as{' '}
              <strong className="font-semibold">Additional Photos</strong> in the right category. You
              confirm before syncing to Google.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onBulkUpload}
          className="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-[var(--r-sm)] border-0 bg-[var(--ink-0)] px-5 py-3 text-[13px] font-semibold text-[var(--cream-1)]"
        >
          Choose Files
          <Icon kind="arrow-r" size={14} stroke={2.2} />
        </button>
      </div>
    </div>
  );
}
